/**
 * Infergraph Design Tokens
 *
 * VC-G7 — VC-G8 BRAND_VISUAL_IDENTITY.md spec 의 token 정의를 코드로 구현.
 * 본 파일이 단일 진실 소스 (single source of truth). MUI theme 및 wrapper component 는 이 token 만 참조.
 *
 * 비전 4축 시그너처 색상:
 * - 에이전틱 AI       → accent.inference
 * - 외부 IT 결합      → accent.flow
 * - 온톨로지 캡처     → accent.graph
 * - Closed-loop 분석  → primary[500]
 */

export const palette = {
  primary: {
    50: '#E3F2FD',
    100: '#BBDEFB',
    500: '#1976D2',
    700: '#1565C0',
    900: '#0D47A1',
  },
  accent: {
    graph: '#7C4DFF',       // ontology graph node / lineage
    inference: '#00BFA5',   // AI inference / agent action
    flow: '#FF9100',        // workflow execution / running state
  },
  neutral: {
    light: {
      0: '#FFFFFF',
      50: '#FAFAFA',
      100: '#F5F5F5',
      200: '#EEEEEE',
      300: '#E0E0E0',
      500: '#9E9E9E',
      700: '#616161',
      900: '#212121',
    },
    dark: {
      0: '#121212',
      50: '#1E1E1E',
      100: '#262626',
      300: '#3D3D3D',
      500: '#8E8E8E',
      700: '#BDBDBD',
      900: '#EEEEEE',
    },
  },
  semantic: {
    light: {
      success: '#2E7D32',
      warning: '#ED6C02',
      error: '#C62828',
      info: '#0288D1',
    },
    dark: {
      success: '#66BB6A',
      warning: '#FFA726',
      error: '#EF5350',
      info: '#29B6F6',
    },
  },
} as const

export const fontFamily = {
  sans: [
    '-apple-system',
    'BlinkMacSystemFont',
    '"Pretendard Variable"',
    'Pretendard',
    'Inter',
    '"Noto Sans KR"',
    '"Noto Sans JP"',
    '"Segoe UI"',
    'Roboto',
    '"Helvetica Neue"',
    'Arial',
    'sans-serif',
  ].join(', '),
  mono: [
    '"JetBrains Mono"',
    '"Fira Code"',
    '"SF Mono"',
    'Menlo',
    'Monaco',
    'Consolas',
    '"Courier New"',
    'monospace',
  ].join(', '),
} as const

/** type-scale: size, lineHeight, weight */
export const typography = {
  display1: { size: 56, lineHeight: 64, weight: 700, letterSpacing: -0.01 },
  display2: { size: 44, lineHeight: 52, weight: 700, letterSpacing: -0.01 },
  heading1: { size: 32, lineHeight: 40, weight: 700, letterSpacing: -0.005 },
  heading2: { size: 24, lineHeight: 32, weight: 600, letterSpacing: 0 },
  heading3: { size: 20, lineHeight: 28, weight: 600, letterSpacing: 0 },
  heading4: { size: 18, lineHeight: 26, weight: 600, letterSpacing: 0 },
  body1: { size: 16, lineHeight: 24, weight: 400, letterSpacing: 0 },
  body2: { size: 14, lineHeight: 20, weight: 400, letterSpacing: 0 },
  caption: { size: 12, lineHeight: 16, weight: 400, letterSpacing: 0.02 },
  monoCode: { size: 13, lineHeight: 20, weight: 400, letterSpacing: 0 },
  monoBlock: { size: 13, lineHeight: 20, weight: 400, letterSpacing: 0 },
} as const

/** 4-base spacing scale */
export const spacing = {
  0: 0,
  1: 4,
  2: 8,
  3: 12,
  4: 16,
  5: 20,
  6: 24,
  8: 32,
  10: 40,
  12: 48,
  16: 64,
  20: 80,
  24: 96,
} as const

export const radius = {
  none: 0,
  sm: 2,
  md: 4,
  lg: 8,
  xl: 12,
  full: 9999,
} as const

export const shadow = {
  none: 'none',
  sm: '0 1px 2px 0 rgba(0,0,0,0.06)',
  md: '0 2px 4px 0 rgba(0,0,0,0.08), 0 1px 2px 0 rgba(0,0,0,0.04)',
  lg: '0 4px 12px 0 rgba(0,0,0,0.10), 0 2px 4px 0 rgba(0,0,0,0.06)',
  xl: '0 12px 32px 0 rgba(0,0,0,0.14), 0 4px 8px 0 rgba(0,0,0,0.08)',
} as const

export const motion = {
  duration: {
    instant: 75,
    fast: 150,
    normal: 200,
    slow: 400,
    loop: 1200,
  },
  easing: {
    default: 'cubic-bezier(0.4, 0, 0.2, 1)',
    decel: 'cubic-bezier(0, 0, 0.2, 1)',
    accel: 'cubic-bezier(0.4, 0, 1, 1)',
    bounce: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
  },
} as const

/** 4축 시각 시그너처 (VC-G8 §8) — 홈 hero (VC-G1) 등에서 재사용 */
export const visionAxes = {
  agenticAi: {
    color: palette.accent.inference,
    motionDuration: motion.duration.loop,
  },
  itInfrastructure: {
    color: palette.accent.flow,
    motionDuration: motion.duration.normal,
  },
  ontologyCapture: {
    color: palette.accent.graph,
    motionDuration: motion.duration.slow,
  },
  closedLoopAnalysis: {
    color: palette.primary[500],
    motionDuration: motion.duration.loop,
  },
} as const

export type ColorMode = 'light' | 'dark'

/**
 * R7 (2026-08-02) — 대시보드 표면/차트/숫자 토큰.
 *
 * 리포트 캔버스가 순백 캔버스 + 민짜 카드(=MUI 기본값)로 렌더되던 것을
 * 표면 위계(옅은 캔버스 / 흰 카드 + hairline + 미세 그림자)로 교정하고,
 * 차트가 recharts 기본색을 쓰던 것을 재무 시맨틱(증가/감소/앵커/강조)으로
 * 통일한다. 위젯은 이 토큰만 참조한다 — 위젯 안 하드코딩 금지.
 */
export const surface = {
  light: {
    canvas: '#F6F7F9',        // 리포트/대시보드 캔버스 (카드 뒤 배경)
    card: '#FFFFFF',
    cardBorder: '#E5E8EC',
    cardHover: '#FDFDFE',
    headerTint: '#F2F4F7',    // 표 헤더 등 옅은 면
  },
  dark: {
    canvas: '#121212',
    card: '#1E1E1E',
    cardBorder: '#333333',
    cardHover: '#242424',
    headerTint: '#262626',
  },
} as const

export const chart = {
  light: {
    increase: '#188758',      // 재무 문법: 증가 = 초록
    decrease: '#C6403D',      // 감소 = 적색
    anchor: '#5B6472',        // 앵커(전월/당월 총액) = 중립 슬레이트
    highlight: '#1976D2',     // 지배 마디 강조 (브랜드)
    connector: '#B9C0C9',     // 워터폴 마디 연결 점선
    grid: '#ECEEF1',
    series: ['#1976D2', '#F29B38', '#7C4DFF', '#00A98F', '#D45087', '#5B6472'],
  },
  dark: {
    increase: '#4CAF7D',
    decrease: '#EF6461',
    anchor: '#8B93A1',
    highlight: '#64B5F6',
    connector: '#4A5158',
    grid: '#2C2F33',
    series: ['#64B5F6', '#FFB74D', '#B39DDB', '#4DD0C4', '#F48FB1', '#8B93A1'],
  },
} as const

/** 숫자 표기 — KPI 큰 숫자/표는 tabular-nums 로 자릿수를 정렬한다. */
export const numeric = {
  fontFeatureSettings: '"tnum" 1, "lnum" 1',
  fontVariantNumeric: 'tabular-nums lining-nums',
  kpiValue: { size: 34, lineHeight: 40, weight: 700, letterSpacing: -0.01 },
} as const

export const tokensForMode = (mode: ColorMode) => ({
  neutral: palette.neutral[mode],
  semantic: palette.semantic[mode],
  surface: surface[mode],
  chart: chart[mode],
})

/**
 * Track3 — design-system primitives (ChipStrip / DetailsDrawer / LandingTemplate)
 *
 * Tone semantics shared across InferOps/LoopEvidence/ReportLibrary/AIBuild surfaces.
 * The four `kind` values map to MUI Chip `color` defaults derived from current usage
 * in the legacy chip files. Item-level `tone` always overrides the kind default.
 *
 * Source of truth for tone -> MUI color mapping (see ChipStrip.tsx):
 *   default → 'default'   (neutral)
 *   success → 'success'
 *   warning → 'warning'
 *   error   → 'error'
 *   info    → 'info'
 */
export type ChipStripKind = 'status' | 'governance' | 'trace' | 'revision'
export type ChipStripTone =
  | 'default'
  | 'success'
  | 'warning'
  | 'error'
  | 'info'

/**
 * Default fallback tone for each chip strip kind. Derived from legacy patterns:
 * - status:     neutral/info — broad agent status badges
 * - governance: info         — most governance chips were info/secondary outlined
 * - trace:      info         — actions/events/workflows counts skewed info-color
 * - revision:   info         — revision counts used secondary; default to info
 */
export const chipStripKindDefaultTone: Record<ChipStripKind, ChipStripTone> = {
  status: 'default',
  governance: 'info',
  trace: 'info',
  revision: 'info',
} as const

/**
 * Visual variant per kind. Matches legacy usage where governance/trace/revision
 * chips were rendered as `variant="outlined"` while status chips mixed filled
 * + outlined depending on severity.
 */
export const chipStripKindVariant: Record<
  ChipStripKind,
  'filled' | 'outlined'
> = {
  status: 'filled',
  governance: 'outlined',
  trace: 'outlined',
  revision: 'outlined',
} as const
